import { useEffect, useMemo } from "react";
import { baueListenIndex, einstandsKarte, gesamtFlaschen, jahrgangTitel, jahrgangsWert, kritikenFuer, regionZuWein, sortiere, winzerZuWein } from "../kern/fachlogik.js";
import { alsWahl } from "../kern/filterwahl.js";
import { fmtEuro, jahrLabel, wertFormat } from "../kern/format.js";
import { SCHEMATA, SORT_OPTIONEN } from "../kern/konstanten.js";
import { useNachwachsen, useWachsendeElemente, usePersistentState } from "../ui/hooks.jsx";
import { BestandAnzeige, Btn, Leer, TextKnopf, TypPunkt, Werkzeugleiste, ZeilenMenue, jahrgangsMenue } from "../ui/primitive.jsx";
import { FilterFelder, useWeinFilter } from "../ui/weinfilter.jsx";
import { t } from "../i18n/index.js";
import { landName } from "../i18n/laender.js";


function KellerAnsicht({ db, vorgabe, nurBestand, geheZu, oeffneModal, loescheJahrgang, setzeFavorit }) {
  const filter = useWeinFilter(db, "keller", vorgabe, nurBestand);
  const { suche, setSuche } = filter;
  const [sortierung, setSortierung] = usePersistentState("keller.sortierung", "name-az");

  /* Vorbelegung aus Verlinkungen („Im Keller zeigen") hat Vorrang vor dem
     gespeicherten Filter — nur wenn tatsächlich eine Vorgabe übergeben wird. */
  useEffect(() => {
    if (vorgabe?.regionId != null) filter.setzer.setRegion(alsWahl(vorgabe.regionId));
    if (vorgabe?.winzerId != null) filter.setzer.setWinzer(alsWahl(vorgabe.winzerId));
  }, [vorgabe?.regionId, vorgabe?.winzerId]);

  const zeilen = useMemo(() => {
    /* Einmal indizieren statt je Zeile die vollen Bewegungs- und
       Kritiklisten zu durchsuchen (siehe kern/fachlogik.js). */
    const index = baueListenIndex(db);
    const liste = [];
    for (const wein of db.weine) {
      const winzer = winzerZuWein(db, wein);
      const region = regionZuWein(db, wein);
      for (const jg of wein.jahrgaenge) liste.push({ wein, winzer, region, jg });
    }
    const gefiltert = liste
      .filter((z) => {
        /* Siehe weine.jsx: Katalogjahrgänge haben nie Bestand. */
        if (nurBestand && !z.wein.nurKatalog && gesamtFlaschen(z.jg) === 0) return false;
        return filter.passt(z.wein, z.jg, z.winzer, z.region);
      });
    const proj = (z) => ({
      name: z.wein.name, jahrMin: z.jg.jahr, jahrMax: z.jg.jahr,
      preis: z.jg.preis == null ? null : z.jg.preis,
      bewertung: index.bewertungJahrgang(z.wein.id, z.jg.id),
      bestand: gesamtFlaschen(z.jg),
      trinkab: z.jg.trinkVon != null ? z.jg.trinkVon : null,
      /* Hier ist die Zeile ein Jahrgang — kein Zusammenfassen nötig.
         Auch leere Jahrgänge behalten ihr Fenster: Wer sie über den
         Schalter „Nur mit Bestand" sichtbar gemacht hat, will sie
         nach ihren eigenen Daten sortiert sehen. */
      trinkbis: z.jg.trinkBis != null ? z.jg.trinkBis : null,
      einlagerung: index.einlagerung(z.wein.id),
    });
    return sortiere(gefiltert, proj, sortierung);
  }, [db, filter.signatur, nurBestand, sortierung]);

  /* Kennzahlen folgen der aktiven Filterung */
  const stats = useMemo(() => {
    let flaschen = 0, wert = 0, positionen = 0;
    const weinIds = new Set();
    const einstand = einstandsKarte(db);
    for (const z of zeilen) {
      const g = gesamtFlaschen(z.jg);
      flaschen += g;
      if (g > 0) positionen += 1;
      wert += jahrgangsWert(z.jg, einstand);
      weinIds.add(z.wein.id);
    }
    return { flaschen, positionen, weine: weinIds.size, wert };
  }, [zeilen, db]);

  /* Lange Listen wachsen beim Scrollen nach — es wird nichts
     geladen, nur weniger auf einmal gerendert. Siehe useNachwachsen. */
  const { sichtbar, marke, alles, gesamt } = useNachwachsen(zeilen);

  /* Die Zeilen als fertige Elemente merken — und beim Nachwachsen
     nur den Zuwachs bauen. Siehe useWachsendeElemente. */
  const zeilenElemente = useWachsendeElemente(zeilen, sichtbar,
    ({ wein, winzer, region, jg }) => {
                const kritiken = kritikenFuer(db, wein.id, null, jg.id);
                return (
                  <tr key={wein.id + "-" + jg.jahr}>
                    <td>
                      <button className="wl-zeilentitel" onClick={() => geheZu({ view: "wein", id: wein.id })}>{wein.name}{wein.favorit && <span className="wl-favorit" title={t("Lieblingswein")}>★</span>}</button>
                      {winzer && (
                        <button className="wl-nebenlink" onClick={() => geheZu({ view: "winzerDetail", id: winzer.id })}>{winzer.name}</button>
                      )}
                    </td>
                    <td><button className="wl-jahrnum wl-jahrlink num" onClick={() => geheZu({ view: "jahrgang", weinId: wein.id, jgId: jg.id })}>{jahrgangTitel(wein, jg)}</button></td>
                    <td><TypPunkt typ={wein.typ} /></td>
                    <td>{region && region.land ? landName(region.land) : "—"}</td>
                    <td>{region
                      ? <TextKnopf onClick={() => geheZu({ view: "region", id: region.id })}>{region.name}</TextKnopf>
                      : "—"}</td>
                    <td className="wl-kritikzelle">
                      {kritiken.length === 0 ? "—" : kritiken.map((k) => (
                        <TextKnopf key={k.id} title={`${k.kritiker} · ${SCHEMATA[k.schema] ? SCHEMATA[k.schema].label : k.schema}`}
                          onClick={() => oeffneModal({ typ: "kritik", objekt: k, kontext: { weinId: wein.id } })}>
                          <span className="num">{wertFormat(k.schema, k.wert)}</span>
                        </TextKnopf>
                      ))}
                    </td>
                    <td className="num">{fmtEuro(jg.preis)}</td>
                    <td>{jg.lagerort || "—"}</td>
                    <td><BestandAnzeige jahrgang={jg} /></td>
                    <td className="wl-rechts">
                      <ZeilenMenue beschriftung={`Aktionen für ${wein.name} ${jahrLabel(jg.jahr)}`}
                        eintraege={jahrgangsMenue({ wein, jg, oeffneModal, loescheJahrgang, setzeFavorit })} />
                    </td>
                  </tr>
                );
    },
    [db, geheZu, oeffneModal, loescheJahrgang, setzeFavorit]);

  return (
    <>
      <Werkzeugleiste
        suche={suche} setSuche={setSuche} sucheLabel={t("Keller durchsuchen …")}
        filterAktiv={filter.aktiv}
        onReset={filter.reset}
        sortOptionen={SORT_OPTIONEN} sortWert={sortierung} setSort={setSortierung}
        aktion={<Btn variante="voll" onClick={() => oeffneModal({ typ: "wein" })}>{t("Wein anlegen")}</Btn>}
        filter={<FilterFelder db={db} f={filter} />}
      />

      <div className="wl-ledger">
        <div><span className="wl-kennzahl num">{stats.flaschen}</span><span className="wl-kennname">{t("Flaschen")}</span></div>
        <div><span className="wl-kennzahl num">{stats.positionen}</span><span className="wl-kennname">{t("Positionen")}</span></div>
        <div><span className="wl-kennzahl num">{stats.weine}</span><span className="wl-kennname">{t("Weine")}</span></div>
        <div><span className="wl-kennzahl num">{fmtEuro(stats.wert)}</span><span className="wl-kennname">{t("Kellerwert")}</span></div>
      </div>

      {zeilen.length === 0 ? (
        <Leer
          text={t("Keine Positionen gefunden. Lege einen Wein an oder passe Suche und Filter an.")}
          aktion={<Btn onClick={() => oeffneModal({ typ: "wein" })}>{t("Wein anlegen")}</Btn>}
        />
      ) : (
        <div className="wl-scroll">
          <table className="wl-tabelle">
            <thead>
              <tr>
                <th>{t("Wein")}</th><th>{t("Jahrgang")}</th><th>{t("Typ")}</th><th>{t("Land")}</th><th>{t("Region")}</th>
                <th>{t("Kritik")}</th><th>{t("Preis")}</th><th>{t("Lager")}</th><th>{t("Bestand")}</th><th className="wl-rechts"> </th>
              </tr>
            </thead>
            <tbody>{zeilenElemente}
            </tbody>
          </table>
        </div>
      )}

      {/* Anker fürs Nachwachsen. Die Zeile sagt zugleich, dass mehr da
          ist als gerade sichtbar — sonst hielte man die Liste für
          vollständig. Steht außerhalb des waagerecht schiebbaren
          Bereichs, damit sie beim Schieben nicht mitwandert. */}
      {!alles && (
        <div className="wl-nachwachsen" ref={marke}>
          {t("{n} von {m} angezeigt — weiter scrollen für mehr", { n: sichtbar.length, m: gesamt })}
        </div>
      )}
    </>
  );
}

export { KellerAnsicht };
